import React, { useState, useEffect, useCallback, useRef } from 'react'
import { MdOutlineFileDownload, MdRefresh, MdPlayArrow, MdPause, MdSkipPrevious, MdSkipNext, MdClose, MdSpeed, MdExpandMore } from 'react-icons/md'
import { HiOutlineCalendar, HiOutlineClock, HiOutlineLocationMarker } from 'react-icons/hi'
import { TbRuler } from 'react-icons/tb'

const API_URL = '/api/tickets/ert-ticket-manager/trip-history-agents-list/'
const TECH_LIST_API_URL = '/api/tickets/ert-ticket-manager/all-history-agents-list/'
const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const PAGE_SIZE = 10

const DATE_OPTIONS = [
  { period: 'today',     label: 'Today' },
  { period: 'yesterday', label: 'Yesterday' },
  { period: '7days',     label: 'Last 7 Days' },
  { period: '30days',    label: 'Last 30 Days' },
  { period: '6months',   label: 'Last 6 Months' },
]

const filterBaseName = (placeholder) => placeholder.replace(/^All /, '')

const BIKE_ICON_URL = `${process.env.PUBLIC_URL}/bike-icon.svg`
const BIKE_ICON_ASPECT = 587 / 1179 // width / height of bike-icon.svg
const BIKE_HEIGHT = 46
const BIKE_WIDTH = Math.round(BIKE_HEIGHT * BIKE_ICON_ASPECT)

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function formatTableDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`
}

function formatTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  let h = d.getHours(), m = d.getMinutes()
  const ap = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ap}`
}

function formatWorkTime(seconds) {
  if (seconds == null || seconds === '') return '—'
  const s = Number(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`
}

function mapStatus(s) {
  if (s === 'checked_out') return 'Completed'
  if (s === 'checked_in') return 'In Progress'
  return s || '—'
}

function fmtCoord(lat, lng) {
  if (!lat || !lng) return '—'
  return `${parseFloat(lat).toFixed(4)}°, ${parseFloat(lng).toFixed(4)}°`
}

function fmtGeoAddress(geo) {
  if (!geo) return ''
  return geo.formatted_address || [geo.area, geo.city, geo.state].filter(Boolean).join(', ')
}

function fmtGeoShort(geo) {
  if (!geo) return ''
  return [geo.area, geo.city].filter(Boolean).join(', ')
}

function transformTrip(r) {
  const checkinCoord = fmtCoord(r.checkin_latitude, r.checkin_longitude)
  const checkoutCoord = fmtCoord(r.checkout_latitude, r.checkout_longitude)
  return {
    id: r.id,
    user_id_raw: r.user?.id || r.id,
    user_name: r.user?.name || '—',
    user_phone: r.user?.phone || '',
    date_raw: r.date,
    date: formatTableDate(r.date),
    checkin_time: formatTime(r.checkin_at),
    checkout_time: r.checkout_at ? formatTime(r.checkout_at) : '',
    total_working_time: formatWorkTime(r.total_work_time),
    checkin_location: fmtGeoShort(r.checkin_geo_data) || checkinCoord,
    checkout_location: fmtGeoShort(r.checkout_geo_data) || checkoutCoord,
    checkin_location_full: fmtGeoAddress(r.checkin_geo_data) || checkinCoord,
    checkout_location_full: fmtGeoAddress(r.checkout_geo_data) || checkoutCoord,
    checkin_lat: r.checkin_latitude ? Number(r.checkin_latitude) : null,
    checkin_lng: r.checkin_longitude ? Number(r.checkin_longitude) : null,
    checkout_lat: r.checkout_latitude ? Number(r.checkout_latitude) : null,
    checkout_lng: r.checkout_longitude ? Number(r.checkout_longitude) : null,
    distance_km: r.total_distance_covered != null ? (r.total_distance_covered / 1000).toFixed(2) : '—',
    status: mapStatus(r.status),
  }
}

// Build route waypoints from API detail response (traces array)
function buildRouteData(data) {
  const traces = (data?.traces || []).filter(t => t.latitude && t.longitude)

  // If traces available use them; fallback to checkin/checkout as two points
  if (traces.length) {
    return {
      waypoints: traces.map(t => ({
        lat: Number(t.latitude),
        lng: Number(t.longitude),
        timestamp: formatTime(t.recorded_at),
      })),
    }
  }

  const pts = []
  if (data?.checkin_latitude && data?.checkin_longitude) {
    pts.push({ lat: Number(data.checkin_latitude), lng: Number(data.checkin_longitude), timestamp: formatTime(data.checkin_at) })
  }
  if (data?.checkout_latitude && data?.checkout_longitude) {
    pts.push({ lat: Number(data.checkout_latitude), lng: Number(data.checkout_longitude), timestamp: formatTime(data.checkout_at) })
  }
  return { waypoints: pts }
}

function loadGoogleMaps() {
  if (window.google?.maps?.Map) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.getElementById('gmaps-ert-sdk')
    if (existing) {
      existing.addEventListener('load', resolve, { once: true })
      existing.addEventListener('error', () => reject(new Error('Maps failed')), { once: true })
      return
    }
    const s = document.createElement('script')
    s.id = 'gmaps-ert-sdk'
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=geometry`
    s.async = true
    s.onload = resolve
    s.onerror = () => reject(new Error('Maps failed'))
    document.head.appendChild(s)
  })
}

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ color: '#6b7280', fontSize: 14, marginTop: 1, flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 13, color: '#111827', fontWeight: 500, lineHeight: 1.4 }}>{value || '—'}</span>
      </div>
    </div>
  )
}

function PageBtn({ children, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 28, height: 28, padding: '0 6px', border: `1px solid ${active ? '#1a6fa8' : '#e5e7eb'}`, borderRadius: 6, background: active ? '#1a6fa8' : '#fff', color: active ? '#fff' : disabled ? '#d1d5db' : '#374151', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: active ? 700 : 400, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </button>
  )
}

function MultiSelectDropdown({ values, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function handler(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter(v => v !== val))
    else onChange([...values, val])
  }

  const label = values.length === 0 ? placeholder
    : values.length === 1 ? (options.find(o => o.value === values[0])?.label || values[0])
    : `${filterBaseName(placeholder)} (${values.length})`

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: 220 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', height: 38, padding: '0 12px', boxSizing: 'border-box',
          background: values.length ? '#E5F4FB' : '#fff',
          border: `1px solid ${values.length ? '#A8D4EE' : '#e5e7eb'}`, borderRadius: 8,
          cursor: 'pointer', fontSize: 13, fontWeight: 500,
          color: values.length ? '#1A6FA8' : '#111827', whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <MdExpandMore style={{ color: '#9ca3af', fontSize: 18, flexShrink: 0 }} />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 220,
        }}>
          {values.length > 0 && (
            <div
              onClick={() => onChange([])}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Clear all ({values.length})
            </div>
          )}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {options.map(opt => {
              const selected = values.includes(opt.value)
              return (
                <div
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#111827',
                    background: selected ? '#EFF6FF' : undefined,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = selected ? '#DBEAFE' : '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = selected ? '#EFF6FF' : '#fff'}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    style={{
                      pointerEvents: 'none', width: 14, height: 14, flexShrink: 0,
                      padding: 0, margin: 0, border: '1px solid #CBD5E1', borderRadius: 3,
                      background: '#fff', accentColor: '#2563EB',
                    }}
                  />
                  {opt.label}
                </div>
              )
            })}
            {options.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>No options</div>}
          </div>
        </div>
      )}
    </div>
  )
}

function MultiSelectSearchDropdown({ fetchJson, values, placeholder, apiUrl, onChange, disabled }) {
  const [open, setOpen]       = useState(false)
  const [items, setItems]     = useState([])
  const [search, setSearch]   = useState('')
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage]       = useState(1)
  const wrapRef               = useRef(null)
  const listRef               = useRef(null)
  const loadingRef            = useRef(false)

  const load = useCallback(async (pg = 1, append = false) => {
    if (!fetchJson || loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(pg) })
      const data = await fetchJson(`${apiUrl}${apiUrl.includes('?') ? '&' : '?'}${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setItems(prev => append ? [...prev, ...results] : results)
      setHasMore(!!data?.next)
      setPage(pg)
    } catch { if (!append) setItems([]) }
    finally { loadingRef.current = false; setLoading(false) }
  }, [fetchJson, apiUrl])

  useEffect(() => {
    if (open) load(1, false)
  }, [open, load])

  useEffect(() => {
    if (!open) return
    function handler(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasMore || loading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) load(page + 1, true)
  }

  const filtered = search.trim()
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items

  const isSelected = slug => values.some(v => v.slug === slug)
  const toggle = (item) => {
    if (isSelected(item.slug)) onChange(values.filter(v => v.slug !== item.slug))
    else onChange([...values, { name: item.name, slug: item.slug }])
  }

  const label = values.length === 0 ? placeholder
    : values.length === 1 ? values[0].name
    : `${filterBaseName(placeholder)} (${values.length})`

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: 220 }}>
      <div
        onClick={() => { if (!disabled) setOpen(o => !o) }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', height: 38, padding: '0 12px', boxSizing: 'border-box',
          background: values.length ? '#E5F4FB' : '#fff',
          border: `1px solid ${values.length ? '#A8D4EE' : '#e5e7eb'}`, borderRadius: 8,
          cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500,
          color: values.length ? '#1A6FA8' : '#111827', whiteSpace: 'nowrap',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <MdExpandMore style={{ color: '#9ca3af', fontSize: 18, flexShrink: 0 }} />
      </div>
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 220,
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 12, color: '#111827', boxSizing: 'border-box' }}
            />
          </div>
          {values.length > 0 && (
            <div
              onClick={() => onChange([])}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Clear all ({values.length})
            </div>
          )}
          <div ref={listRef} onScroll={handleScroll} style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.map(item => {
              const selected = isSelected(item.slug)
              return (
                <div
                  key={item.id || item.slug}
                  onClick={() => toggle(item)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#111827',
                    background: selected ? '#EFF6FF' : undefined,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = selected ? '#DBEAFE' : '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = selected ? '#EFF6FF' : '#fff'}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    style={{
                      pointerEvents: 'none', width: 14, height: 14, flexShrink: 0,
                      padding: 0, margin: 0, border: '1px solid #CBD5E1', borderRadius: 3,
                      background: '#fff', accentColor: '#2563EB',
                    }}
                  />
                  {item.name}
                </div>
              )
            })}
            {loading && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>}
            {!loading && filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>No results</div>}
            {hasMore && !loading && <div style={{ padding: '6px 12px', fontSize: 10, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll for more</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TripHistoryPage({ fetchJson }) {
  const [pageTrips, setPageTrips] = useState([])   // trips for the current server page only
  const [serverTotalCount, setServerTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [dateFilter, setDateFilter] = useState(() => ({ period: 'today', label: 'Today', fromDate: todayISO(), toDate: todayISO() }))
  const [dateOpen, setDateOpen] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const dateRef = useRef(null)
  const [technicians, setTechnicians] = useState([])
  const [selectedTechs, setSelectedTechs] = useState([])
  const [cityFilters, setCityFilters] = useState([])
  const [page, setPage] = useState(1)

  // Route map
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [routeData, setRouteData] = useState(null)
  const [routeLoading, setRouteLoading] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const [mapError, setMapError] = useState(false)
  const mapElRef = useRef(null)
  const mapObjRef = useRef(null)
  const markerRef = useRef(null)
  const polylinesRef = useRef([])
  const directionsRendererRef = useRef(null)

  // Playback
  const [playPercent, setPlayPercent] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playSpeed, setPlaySpeed] = useState(1)
  const playTimerRef = useRef(null)

  // Export
  const [exportLoading, setExportLoading] = useState(false)
  const [exportDropOpen, setExportDropOpen] = useState(false)
  const exportPollRef = useRef(null)
  const exportDropRef = useRef(null)
  useEffect(() => () => { if (exportPollRef.current) clearInterval(exportPollRef.current) }, [])

  useEffect(() => {
    if (!exportDropOpen) return
    function handler(e) { if (exportDropRef.current && !exportDropRef.current.contains(e.target)) setExportDropOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [exportDropOpen])

  useEffect(() => {
    if (!dateOpen) return
    function handler(e) { if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dateOpen])

  // ── Derived state ────────────────────────────────────────────────────────────
  // Technician filtering is done server-side (agent_number sent to the trip
  // history API), so the page's results are shown as-is.
  const totalCount = serverTotalCount
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const pagedTrips = pageTrips
  const startEntry = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endEntry = Math.min(page * PAGE_SIZE, totalCount)

  // ── Fetch the full technician list once (independent of date/page) ─────────
  // Response is a plain array of { id, name, phone } — the phone number is
  // what the trip history API expects back as `agent_number`.
  useEffect(() => {
    fetchJson(TECH_LIST_API_URL)
      .then(res => {
        const list = Array.isArray(res) ? res : (res?.data || res?.results || [])
        setTechnicians(list.filter(t => t.phone).map(t => ({
          agent_number: t.phone,
          name: t.name || t.phone,
        })))
      })
      .catch(() => setTechnicians([]))
  }, [fetchJson])

  // ── Fetch one page of trips at a time ───────────────────────────────────────
  // Real server-side pagination: only the page currently being viewed is
  // requested. Clicking another page number triggers a fresh request for it.
  const fetchTrips = useCallback(() => {
    setLoading(true)
    setSelectedTrip(null)
    setRouteData(null)
    fetchJson(API_URL, {
      method: 'POST',
      body: JSON.stringify({
        ...(dateFilter.fromDate ? { from_date: dateFilter.fromDate } : {}),
        ...(dateFilter.toDate   ? { to_date: dateFilter.toDate }     : {}),
        page,
        page_size: PAGE_SIZE,
        ...(selectedTechs.length ? { agent_number: selectedTechs.join(',') } : {}),
        ...(cityFilters.length ? { city: cityFilters.map(c => c.slug).join(',') } : {}),
      }),
    })
      .then(res => {
        const results = res?.data?.results || []
        const trips = results.map(transformTrip)
        setPageTrips(trips)
        setServerTotalCount(res?.data?.total_count ?? trips.length)
      })
      .catch(() => { setPageTrips([]); setServerTotalCount(0) })
      .finally(() => setLoading(false))
  }, [fetchJson, dateFilter, page, selectedTechs, cityFilters])

  useEffect(() => { fetchTrips() }, [fetchTrips])

  // ── Actions ─────────────────────────────────────────────────────────────────
  function handleReset() {
    setDateFilter({ period: 'today', label: 'Today', fromDate: todayISO(), toDate: todayISO() })
    setSelectedTechs([])
    setCityFilters([])
    setPage(1)
  }

  function selectPeriod(opt) {
    const today = new Date()
    const fmt = d => d.toISOString().slice(0, 10)
    let fromDate = fmt(today)
    let toDate = fmt(today)
    if (opt.period === 'yesterday') {
      const d = new Date(today); d.setDate(d.getDate() - 1)
      fromDate = fmt(d); toDate = fmt(d)
    }
    if (opt.period === '7days')   { const d = new Date(today); d.setDate(d.getDate() - 6);  fromDate = fmt(d) }
    if (opt.period === '30days')  { const d = new Date(today); d.setDate(d.getDate() - 29); fromDate = fmt(d) }
    if (opt.period === '6months') { const d = new Date(today); d.setMonth(d.getMonth() - 6); fromDate = fmt(d) }
    setDateFilter({ period: opt.period, label: opt.label, fromDate, toDate })
    setDateOpen(false)
    setPage(1)
  }

  function applyCustom() {
    if (!customFrom || !customTo) return
    setDateFilter({ period: 'custom', label: `${customFrom} – ${customTo}`, fromDate: customFrom, toDate: customTo })
    setDateOpen(false)
    setPage(1)
  }

  async function handleExport(fileType) {
    setExportDropOpen(false)
    if (exportLoading) return
    setExportLoading(true)
    try {
      const params = new URLSearchParams({ file_type: fileType })
      if (dateFilter.fromDate) params.set('from_date', dateFilter.fromDate)
      if (dateFilter.toDate)   params.set('to_date', dateFilter.toDate)
      if (selectedTechs.length) params.set('agent_number', selectedTechs.join(','))
      if (cityFilters.length) params.set('city', cityFilters.map(c => c.slug).join(','))
      const data = await fetchJson(`/api/tickets/ert-ticket-manager/export-trip-history-initiate/?${params}`)
      const taskId = data?.task_id
      if (!taskId) throw new Error('No task_id returned')

      exportPollRef.current = setInterval(async () => {
        try {
          const resp = await fetch(`/api/tickets/ert-ticket-manager/export-trip-history/?task_id=${taskId}`, { credentials: 'include' })
          if (resp.status === 200) {
            clearInterval(exportPollRef.current)
            exportPollRef.current = null
            setExportLoading(false)

            const contentType = resp.headers.get('content-type') || ''
            if (contentType.includes('application/json')) {
              const result = await resp.json()
              if (result?.file_url) {
                const a = document.createElement('a')
                a.href = result.file_url
                a.download = ''
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              } else {
                alert('Export finished but no file URL was returned.')
              }
            } else {
              // Server sent the file itself
              const blob = await resp.blob()
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `trip-history-${dateFilter.fromDate || 'all'}_to_${dateFilter.toDate || 'all'}.${fileType === 'excel' ? 'xlsx' : 'csv'}`
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              URL.revokeObjectURL(url)
            }
          }
          // 202 = still processing, keep polling
        } catch { /* transient error, keep polling */ }
      }, 3000)
    } catch (err) {
      setExportLoading(false)
      alert(err?.message || 'Failed to initiate export.')
    }
  }

  function handleViewMap(trip) {
    setSelectedTrip(trip)
    setRouteData(null)
    setMapReady(false)
    setMapError(false)
    setPlayPercent(0)
    setIsPlaying(false)
    clearInterval(playTimerRef.current)
    polylinesRef.current.forEach(p => p.setMap?.(null))
    polylinesRef.current = []
    directionsRendererRef.current?.setMap(null)
    directionsRendererRef.current = null
    markerRef.current = null
    mapObjRef.current = null
    setRouteLoading(true)
    fetchJson(API_URL, {
      method: 'POST',
      body: JSON.stringify({ checkin_id: trip.id, date: trip.date_raw }),
    })
      .then(res => setRouteData(buildRouteData(res?.data)))
      .catch(() => setRouteData(null))
      .finally(() => setRouteLoading(false))
    setTimeout(() => {
      document.getElementById('th-route-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
  }

  function closeRoutePanel() {
    setSelectedTrip(null)
    setRouteData(null)
    setMapReady(false)
    setMapError(false)
    setIsPlaying(false)
    clearInterval(playTimerRef.current)
    polylinesRef.current.forEach(p => p.setMap?.(null))
    polylinesRef.current = []
    directionsRendererRef.current?.setMap(null)
    directionsRendererRef.current = null
    markerRef.current = null
    mapObjRef.current = null
  }

  // ── Map init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!routeData?.waypoints?.length) return
    const t = setTimeout(() => {
      if (!mapElRef.current) return
      setMapError(false)
      loadGoogleMaps()
        .then(() => {
          const waypoints = routeData.waypoints
          const center = { lat: waypoints[0].lat, lng: waypoints[0].lng }

          const map = new window.google.maps.Map(mapElRef.current, {
            center, zoom: 13,
            mapTypeControl: false, fullscreenControl: true, streetViewControl: false,
          })
          mapObjRef.current = map

          // Check-in pin (green) — kept above the bike marker so it's never hidden
          // when the bike sits on the same spot (e.g. playhead at 0%).
          new window.google.maps.Marker({
            position: center, map, title: 'Check-in', zIndex: 15,
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: '#22c55e', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
          })

          // Checkout pin (red) — only if different from check-in
          const last = waypoints[waypoints.length - 1]
          if (waypoints.length > 1) {
            new window.google.maps.Marker({
              position: { lat: last.lat, lng: last.lng }, map, title: 'Checkout', zIndex: 15,
              icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: '#ef4444', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
            })
          }

          // Small dot for each unique intermediate trace point (dedup by ~1m precision,
          // so a technician holding still for 10+ min doesn't stack multiple markers).
          // Kept above the bike marker (zIndex 12 > 10) so it stays visible even when
          // the bike passes directly over a recorded stop.
          const traceSeen = new Set()
          traceSeen.add(`${waypoints[0].lat.toFixed(5)},${waypoints[0].lng.toFixed(5)}`)
          traceSeen.add(`${last.lat.toFixed(5)},${last.lng.toFixed(5)}`)
          waypoints.forEach(p => {
            const key = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`
            if (traceSeen.has(key)) return
            traceSeen.add(key)
            new window.google.maps.Marker({
              position: { lat: p.lat, lng: p.lng }, map, zIndex: 12,
              title: p.timestamp || '',
              icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: '#7c3aed', fillOpacity: 0.85, strokeColor: '#fff', strokeWeight: 1.5 },
            })
          })

          // Bike moving marker — same icon as capture rider location map
          markerRef.current = new window.google.maps.Marker({
            position: center, map, zIndex: 10,
            icon: {
              url: BIKE_ICON_URL,
              scaledSize: new window.google.maps.Size(BIKE_WIDTH, BIKE_HEIGHT),
              anchor: new window.google.maps.Point(BIKE_WIDTH / 2, BIKE_HEIGHT),
            },
          })

          // Draw actual road route using Directions API; fall back to raw polyline if denied
          const renderer = new window.google.maps.DirectionsRenderer({
            map,
            suppressMarkers: true,
            polylineOptions: { strokeColor: '#2563eb', strokeWeight: 4, strokeOpacity: 0.85 },
          })
          directionsRendererRef.current = renderer

          const MAX_INTERMEDIATE = 23
          const middle = waypoints.slice(1, -1)
          const step = middle.length > MAX_INTERMEDIATE ? Math.ceil(middle.length / MAX_INTERMEDIATE) : 1
          const intermediate = middle.filter((_, i) => i % step === 0).slice(0, MAX_INTERMEDIATE)

          new window.google.maps.DirectionsService().route(
            {
              origin: { lat: waypoints[0].lat, lng: waypoints[0].lng },
              destination: { lat: last.lat, lng: last.lng },
              waypoints: intermediate.map(p => ({ location: { lat: p.lat, lng: p.lng }, stopover: false })),
              travelMode: window.google.maps.TravelMode.DRIVING,
              optimizeWaypoints: false,
            },
            (result, status) => {
              if (status === 'OK') {
                renderer.setDirections(result)
              } else {
                // Fallback: raw GPS trace as polyline
                polylinesRef.current.push(new window.google.maps.Polyline({
                  path: waypoints.map(p => ({ lat: p.lat, lng: p.lng })),
                  strokeColor: '#2563eb', strokeWeight: 4, strokeOpacity: 0.85, map,
                }))
              }
            }
          )

          const bounds = new window.google.maps.LatLngBounds()
          waypoints.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }))
          map.fitBounds(bounds)
          setMapReady(true)
        })
        .catch(() => setMapError(true))
    }, 50)
    return () => clearTimeout(t)
  }, [routeData])

  // Move bike marker along the scrubber timeline
  useEffect(() => {
    if (!mapReady || !routeData?.waypoints?.length || !markerRef.current) return
    const pts = routeData.waypoints
    const idx = Math.min(Math.floor((playPercent / 100) * (pts.length - 1)), pts.length - 1)
    markerRef.current.setPosition({ lat: pts[idx].lat, lng: pts[idx].lng })
  }, [playPercent, mapReady, routeData])

  // Playback timer
  useEffect(() => {
    clearInterval(playTimerRef.current)
    if (!isPlaying) return
    playTimerRef.current = setInterval(() => {
      setPlayPercent(p => {
        if (p >= 100) { setIsPlaying(false); return 100 }
        return Math.min(p + 0.5 * playSpeed, 100)
      })
    }, 100)
    return () => clearInterval(playTimerRef.current)
  }, [isPlaying, playSpeed])

  useEffect(() => () => clearInterval(playTimerRef.current), [])

  function getScrolledTime() {
    if (!routeData?.waypoints?.length) return '—'
    const pts = routeData.waypoints
    const idx = Math.min(Math.floor((playPercent / 100) * (pts.length - 1)), pts.length - 1)
    return pts[idx]?.timestamp || '—'
  }

  function getPageNumbers() {
    if (totalPages <= 6) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (page <= 3) return [1, 2, 3, '...', totalPages]
    if (page >= totalPages - 2) return [1, '...', totalPages - 2, totalPages - 1, totalPages]
    return [1, '...', page, '...', totalPages]
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', height: '100%', overflowY: 'auto', background: '#f8fafc', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes th-spin { to { transform: rotate(360deg); } }
        .th-icon-btn { background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
        .th-icon-btn:hover { background: #f3f4f6; }
        .th-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .th-viewmap-btn:hover { background: #dbeafe !important; }
        .th-row:hover td { background: #f0f9ff !important; }
      `}</style>

      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Trip History</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Monitor technician movement and trip details</p>
      </div>

      {/* Filters bar */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        {/* Date */}
        <div ref={dateRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Date</span>
          <button
            onClick={() => setDateOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: 210, height: 38,
              padding: '0 10px', boxSizing: 'border-box', cursor: 'pointer',
              border: `1px solid ${dateFilter.period ? '#A8D4EE' : '#e5e7eb'}`, borderRadius: 8,
              background: dateFilter.period ? '#E5F4FB' : '#fff',
              fontSize: 13, color: dateFilter.period ? '#1A6FA8' : '#111827', fontWeight: 500,
            }}
          >
            <HiOutlineCalendar style={{ color: '#4338ca', fontSize: 16, flexShrink: 0 }} />
            <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dateFilter.label}</span>
            <MdExpandMore style={{ color: '#9ca3af', fontSize: 18, flexShrink: 0 }} />
          </button>
          {dateOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100,
              background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,.1)', minWidth: 220,
            }}>
              <div style={{ padding: '6px 0', borderBottom: '1px solid #E0E7EF' }}>
                <div
                  onClick={() => { setDateFilter({ period: '', label: 'All Dates', fromDate: '', toDate: '' }); setDateOpen(false); setPage(1) }}
                  style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: !dateFilter.period ? '#0090D9' : '#2B2B2B', background: !dateFilter.period ? '#E5F4FB' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = !dateFilter.period ? '#E5F4FB' : 'transparent'}
                >All Dates</div>
                {DATE_OPTIONS.map(opt => (
                  <div key={opt.period} onClick={() => selectPeriod(opt)}
                    style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: dateFilter.period === opt.period ? '#0090D9' : '#2B2B2B', background: dateFilter.period === opt.period ? '#E5F4FB' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = dateFilter.period === opt.period ? '#E5F4FB' : 'transparent'}
                  >{opt.label}</div>
                ))}
              </div>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>Custom Range</div>
                <div style={{ marginBottom: 6 }}>
                  <label style={{ fontSize: 11, color: '#5A6672', display: 'block', marginBottom: 3 }}>From</label>
                  <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: 4, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, color: '#5A6672', display: 'block', marginBottom: 3 }}>To</label>
                  <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                    style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: 4, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <button onClick={applyCustom} style={{ width: '100%', padding: '6px', background: '#0090D9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
              </div>
            </div>
          )}
        </div>

        {/* Technician */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Technician</span>
          <MultiSelectDropdown
            values={selectedTechs}
            onChange={vals => { setSelectedTechs(vals); setPage(1) }}
            options={technicians.map(t => ({ value: t.agent_number, label: t.name }))}
            placeholder="All Technicians"
          />
        </div>

        {/* City */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>City</span>
          <MultiSelectSearchDropdown
            fetchJson={fetchJson}
            values={cityFilters}
            placeholder="All Cities"
            apiUrl="/api/geographies/geography/cities"
            onChange={vals => { setCityFilters(vals); setPage(1) }}
          />
        </div>

        {/* Buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={handleReset} style={{ height: 38, padding: '0 22px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            Reset
          </button>
          <div ref={exportDropRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setExportDropOpen(o => !o)}
              disabled={exportLoading}
              style={{ height: 38, padding: '0 18px', border: 'none', borderRadius: 8, background: '#1a6fa8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: exportLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: exportLoading ? 0.7 : 1 }}
            >
              <MdOutlineFileDownload style={{ fontSize: 16 }} /> {exportLoading ? 'Exporting…' : 'Export Data'}
              {!exportLoading && <span style={{ fontSize: 9 }}>▼</span>}
            </button>
            {exportDropOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,.1)', minWidth: 140, overflow: 'hidden',
              }}>
                {[
                  { label: '📊 Excel (.xlsx)', value: 'excel' },
                  { label: '📄 CSV (.csv)',    value: 'csv'   },
                ].map(opt => (
                  <div
                    key={opt.value}
                    onClick={() => handleExport(opt.value)}
                    style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: '#111827' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F9FF'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: selectedTrip ? 16 : 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['User Name', 'Date', 'Check-in Time', 'Checkout Time', 'Working Time', 'Check-in Location', 'Checkout Location', 'Distance (KM)', 'Status', 'View in Map'].map(col => (
                  <th key={col} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', fontSize: 12, borderBottom: '1px solid #e5e7eb' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#1a6fa8', borderRadius: '50%', animation: 'th-spin 0.75s linear infinite' }} />
                </td></tr>
              ) : pagedTrips.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No trips found for the selected date</td></tr>
              ) : pagedTrips.map((trip, i) => (
                <tr key={trip.id} className="th-row" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 14px', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{trip.user_name}</div>
                    {trip.user_phone && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{trip.user_phone}</div>}
                  </td>
                  <td style={{ padding: '12px 14px', color: '#374151', whiteSpace: 'nowrap', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>{trip.date}</td>
                  <td style={{ padding: '12px 14px', color: '#374151', whiteSpace: 'nowrap', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>{trip.checkin_time}</td>
                  <td style={{ padding: '12px 14px', color: '#374151', whiteSpace: 'nowrap', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>{trip.checkout_time || '—'}</td>
                  <td style={{ padding: '12px 14px', color: '#374151', whiteSpace: 'nowrap', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>{trip.total_working_time}</td>
                  <td style={{ padding: '12px 14px', maxWidth: 200, background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }} title={trip.checkin_location_full}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trip.checkin_location}</span>
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', maxWidth: 200, background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }} title={trip.checkout_location_full}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: trip.checkout_location === '—' ? '#d1d5db' : '#ef4444', flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trip.checkout_location}</span>
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#111827', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>{trip.distance_km}</td>
                  <td style={{ padding: '12px 14px', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>
                    <span style={{ padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: trip.status === 'Completed' ? '#dcfce7' : '#fef9c3', color: trip.status === 'Completed' ? '#16a34a' : '#a16207', whiteSpace: 'nowrap' }}>
                      {trip.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', background: selectedTrip?.id === trip.id ? '#eff6ff' : 'transparent' }}>
                    <button className="th-viewmap-btn" onClick={() => handleViewMap(trip)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', border: '1px solid #bfdbfe', borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <HiOutlineLocationMarker style={{ fontSize: 13 }} /> View Map
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalCount > 0 && (
          <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f3f4f6', fontSize: 13, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: '#6b7280' }}>Showing {startEntry} to {endEntry} of {totalCount} entries</span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
              {getPageNumbers().map((p, i) =>
                p === '...'
                  ? <span key={`d${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                  : <PageBtn key={p} active={page === p} onClick={() => setPage(p)}>{p}</PageBtn>
              )}
              <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
            </div>
          </div>
        )}
      </div>

      {/* Route panel */}
      {selectedTrip && (
        <div id="th-route-panel" style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.09)', overflow: 'hidden' }}>

          {/* Panel header */}
          <div style={{ padding: '13px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Trip Route – {selectedTrip.user_name}</span>
              <span style={{ padding: '2px 10px', borderRadius: 12, background: selectedTrip.status === 'Completed' ? '#dcfce7' : '#fef9c3', color: selectedTrip.status === 'Completed' ? '#16a34a' : '#a16207', fontSize: 12, fontWeight: 600 }}>
                {selectedTrip.status}
              </span>
            </div>
            <button onClick={closeRoutePanel} style={{ width: 28, height: 28, border: 'none', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MdClose style={{ fontSize: 16 }} />
            </button>
          </div>

          <div style={{ display: 'flex' }}>
            {/* Left info */}
            <div style={{ width: 210, flexShrink: 0, padding: '18px 16px', borderRight: '1px solid #f3f4f6', overflowY: 'auto' }}>
              <InfoRow icon={<span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />} label="Check-in Location" value={selectedTrip.checkin_location_full} />
              <InfoRow icon={<span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />} label="Checkout Location" value={selectedTrip.checkout_location_full} />
              <InfoRow icon={<TbRuler style={{ fontSize: 15 }} />} label="Distance Travelled" value={selectedTrip.distance_km !== '—' ? `${selectedTrip.distance_km} KM` : '—'} />
              <InfoRow icon={<HiOutlineClock style={{ fontSize: 15 }} />} label="Total Working Time" value={selectedTrip.total_working_time} />
              <InfoRow icon={<HiOutlineCalendar style={{ fontSize: 15 }} />} label="Date" value={selectedTrip.date} />
              <InfoRow icon={<HiOutlineClock style={{ fontSize: 15 }} />} label="Check-in Time" value={selectedTrip.checkin_time} />
              <InfoRow icon={<HiOutlineClock style={{ fontSize: 15 }} />} label="Checkout Time" value={selectedTrip.checkout_time} />
            </div>

            {/* Map + controls */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {/* Timeline */}
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 1 }}>Check-in Time</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{selectedTrip.checkin_time}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 1 }}>Scrolled Time</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{getScrolledTime()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 1 }}>Checkout Time</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                      {selectedTrip.checkout_time || <span style={{ color: '#ef4444', fontSize: 11 }}>● LIVE</span>}
                    </div>
                  </div>
                </div>
                <input type="range" min={0} max={100} value={playPercent} onChange={e => { setPlayPercent(Number(e.target.value)); setIsPlaying(false) }} style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer', margin: '4px 0' }} />
                {routeData?.waypoints?.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                    <span>{routeData.waypoints[0]?.timestamp}</span>
                    <span>{routeData.waypoints[Math.floor(routeData.waypoints.length / 2)]?.timestamp}</span>
                    <span>{routeData.waypoints[routeData.waypoints.length - 1]?.timestamp}</span>
                  </div>
                )}
              </div>

              {/* Map */}
              <div style={{ position: 'relative', flex: 1, minHeight: 320 }}>
                {routeLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', zIndex: 5, gap: 10 }}>
                    <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#1a6fa8', borderRadius: '50%', animation: 'th-spin 0.75s linear infinite' }} />
                    <span style={{ fontSize: 13, color: '#6b7280' }}>Loading route…</span>
                  </div>
                )}
                {!routeLoading && routeData && routeData.waypoints.length === 0 && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#9ca3af', fontSize: 14 }}>No location traces available for this trip</div>
                )}
                {!routeLoading && !routeData && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#9ca3af', fontSize: 14 }}>
                    {mapError ? 'Map unavailable — check API key' : 'Route data unavailable'}
                  </div>
                )}
                <div ref={mapElRef} style={{ width: '100%', height: '100%', minHeight: 320 }} />
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Export processing popup */}
      {exportLoading && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '32px 36px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 280,
          }}>
            <div style={{
              width: 40, height: 40, border: '3px solid #E2E8F0',
              borderTop: '3px solid #1a6fa8', borderRadius: '50%',
              animation: 'th-spin 0.8s linear infinite',
            }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Processing export request</div>
            <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'center' }}>
              Preparing your file. This may take a few seconds…
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
